import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/api/query-keys";
import type { PaginatedResponse } from "@/api/types";
import { api } from "@/lib/api";
import type {
	ChartDataPoint,
	ChartParams,
	HeatMeterDetail,
	HeatMeterRow,
	TableParams,
} from "./types";
import { mapChartPoints } from "./utils";

export function useHeatChart(
	params: ChartParams,
): UseQueryResult<ChartDataPoint[], Error> {
	return useQuery({
		queryKey: queryKeys.districts.heatChart(params),
		queryFn: async (): Promise<ChartDataPoint[]> => {
			const res = await api.get<{ dt: string; sum: number }[]>(
				"/api/districts/heat_chart/",
				{ params },
			);
			return mapChartPoints(res.data);
		},
	});
}

export function useHeatTable(
	params: TableParams,
): UseQueryResult<PaginatedResponse<HeatMeterRow>, Error> {
	return useQuery({
		queryKey: queryKeys.districts.heatTable(params),
		queryFn: async (): Promise<PaginatedResponse<HeatMeterRow>> => {
			const res = await api.get<PaginatedResponse<HeatMeterRow>>(
				"/api/districts/heat_table/",
				{ params },
			);
			return res.data;
		},
	});
}

export function useHeatMeterDetail(
	serialNumber: string,
): UseQueryResult<HeatMeterDetail, Error> {
	return useQuery({
		queryKey: queryKeys.districts.heatMeterDetail(serialNumber),
		queryFn: async (): Promise<HeatMeterDetail> => {
			const res = await api.get<HeatMeterDetail>(
				`/api/districts/heat_meter_detail/${serialNumber}/`,
			);
			return res.data;
		},
		enabled: !!serialNumber,
	});
}
